<template>
  <div class="grid-x ship-it-info">
    <Radio
      class="ship-radio-button"
      :name="page === 'bag' ? `fulfillment-${productId}-bag-${bagItemId}` : `fulfillment-${productId}`"
      :options="options"
      spacing="margin-bottom-z"
      v-bind="$attrs"
    />
    <template v-if="radioLabels.shipTo && !isEGiftCard">
      <button
        aria-haspopup="dialog"
        :aria-label="ariaLabel"
        class="link-button zipcode-button"
        @click="handleZipCodeSlideout"
      >
        {{ currentZipCode }}
      </button>
    </template>
  </div>

  <div
    class="radio-label ship-radio-label"
  >
    <p
      v-if="radioLabels.shipFromSeller"
      class="margin-left-m marketplace-seller"
    >
      {{ radioLabels.shipFromSeller }}
    </p>

    <div
      v-if="isLowStock"
      class="margin-left-m low-stock-copy"
    >
      <span class="low-stock">{{ `${lowStockLabel[0]} ` }}</span> <span
        class="availability-label"
        :class="{display: productAvailabilityInfo.orderType.toLowerCase() !== 'drop' }"
      >{{ lowStockLabel[1] }} </span>
    </div>

    <div
      v-else-if="!options[0].disabled"
      class="margin-left-m ships-within"
      :class="{disabled: options[0].disabled, display: copyClarityEnabled && copyClarityVueEnabled}"
    >
      {{ radioLabels.labelText }}
    </div>
    <div
      v-if="badgingMessage && !options[0].disabled"
      class="event-badging margin-left-m"
      :style="{color: badgingColor}"
    >
      {{ badgingMessage }}
    </div>
    <div
      v-if="!currentZipCode && !isInternationalShip && radioLabels.shipAvailable"
      class="margin-left-m no-zipcode"
    >
      Enter your <button
        class="link-button"
        aria-label="Enter zipcode"
        @click="handleZipCodeSlideout"
      >
        zip code
      </button> to see delivery availability.
    </div>
    <div
      v-if="radioLabels.deliveryDay && !isEGiftCard"
      class="sdd-shipping-message grid-x align-middle"
      :class="{'event-badging': sddColorForBadging}"
      :style="{color: sddColorForBadging}"
    >
      <span class="shipping-message">{{ sddMessageForBadging || radioLabels.deliveryDay }}</span>
    </div>
  </div>
  <ZipcodeSlideout
    v-if="zipcodeSlideoutShown && !isEGiftCard && !isFurnitureShipping"
    :id="productId"
    v-model:zipCode="currentZipCode"
    :product-availability-info="productAvailabilityInfo"
    :page="page"
    :update-ship-zip-code-callback="updateShipZipCodeCallback"
    @update:zipCode="onUpdateZipcode"
    @closed:zipCode="onSlideoutClose"
    @vue:mounted="slideoutMount"
  />
</template>

<script>
import {
  ref, watch, h, computed,
  nextTick,
} from 'vue';
import { useStore } from 'vuex';
import Radio from '@atomic-ui/radio';
import generateShipMessage from '../../utils/generateShipMessage';
import ShipItRadioLabelText from './ShipItRadioLabelText';
import ZipcodeSlideout from '../zipcodeSlideout';
import eligibilityCheckForSTHBadging from '../../utils/eligibilityCheckForSTHBadging';
import generateSDDMessageForBadging from '../../utils/generateSDDMessageForBadging';

export default {
  name: 'ShipIt',
  components: {
    Radio,
    ZipcodeSlideout: ZipcodeSlideout(),
  },
  inheritAttrs: false,
  props: {
    productId: {
      type: String,
      required: true,
    },
    bagItemId: {
      type: String,
      required: true,
    },
    productAvailabilityInfo: {
      type: Object,
      required: true,
    },
    storeDetails: {
      type: Object,
      required: true,
    },
    zipCode: {
      type: String,
      default: '',
    },
    isInternationalShip: {
      type: Boolean,
    },
    page: {
      type: String,
      default: '',
    },
    isEGiftCard: {
      type: Boolean,
    },
    isParcelShipping: {
      type: Boolean,
    },
    isFurnitureShipping: {
      type: Boolean,
    },
    eGiftCardInfo: {
      type: Object,
      default: () => ({}),
    },
    bagItems: {
      type: Array,
      default: () => [],
    },
    pickupStoreDetails: {
      type: Object,
      default: () => ({}),
    },
    updateShipZipCodeCallback: {
      type: Function,
    },
    toggleLoaderForSlideout: {
      type: Function,
    },
    freeShippingEligible: {
      type: Boolean,
      default: false,
    },
    copyClarityVueEnabled: {
      type: Boolean,
      default: false,
    },
    shipEventData: {
      type: Object,
      default: null,
    },
    deliveryDays: {
      type: Object,
      default: null,
    },
    productCategoryId: {
      type: String,
      required: true,
    },
    sddEventData: {
      type: Object,
      default: null,
    },
  },
  emits: ['option-change', 'updated:zipCode'],
  setup(props, { emit }) {
    const options = ref([]);
    const radioLabels = ref({});
    const zipcodeSlideoutShown = ref(false);
    const store = useStore();
    const currentZipCode = ref(props.zipCode?.split('-')[0]);
    const ariaLabel = ref(`Change Zip Code ${ currentZipCode.value}`);
    const previouslyFocusedElement = ref(null);
    const isLowStock = ref(false);
    const lowStockLabel = ref([]);
    const copyClarityEnabled = ref(false);
    const brand = computed(() => process.env.ENTRY_POINT.toLowerCase());
    const badgingMessage = ref('');
    const badgingColor = ref('');
    const sddMessageForBadging = ref('');
    const sddColorForBadging = ref('');

    const zipcodeSlideoutMounted = () => {
      store.commit('slideoutStore/TOGGLE', `zipcode-slideout-${props.productId}`);
    };

    const slideoutMount = () => {
      emit('toggleLoaderForSlideout', false);
      requestAnimationFrame(zipcodeSlideoutMounted);
    };

    const handleZipCodeSlideout = () => {
      emit('toggleLoaderForSlideout', true);
      zipcodeSlideoutShown.value = true;
      previouslyFocusedElement.value = document.activeElement;
    };

    const onUpdateZipcode = async (slideoutZipCode, emitZipcode) => {
      zipcodeSlideoutShown.value = false;
      currentZipCode.value = slideoutZipCode;
      if (emitZipcode) {
        emit('updated:zipCode', currentZipCode.value);
      }
      ariaLabel.value = `The new zipcode is ${ currentZipCode.value }. Change zipcode button.`;
      await nextTick();
      previouslyFocusedElement.value?.focus();
    };

    const onSlideoutClose = () => {
      zipcodeSlideoutShown.value = false;
      previouslyFocusedElement?.value?.focus();
    };

    watch(() => props.zipCode, (data) => {
      if (props.zipCode !== currentZipCode.value) {
        currentZipCode.value = data;
      }
    });

    watch(() => [props.productAvailabilityInfo, props.storeDetails, props.eGiftCardInfo, currentZipCode.value, props.isInternationalShip,
      props.freeShippingEligible, props.sddEventData, props.shipEventData], (data) => {
      const {
        shipText, label, value, disabled, shipFromSeller, sddLabel, shipAvailable,
      } = generateShipMessage({
        productInfo: data[0],
        storeInfo: data[1],
        eGiftCardInfo: data[2],
      },
      currentZipCode.value,
      props.isInternationalShip,
      props.isEGiftCard,
      props.isParcelShipping,
      props.isFurnitureShipping,
      brand.value);
      radioLabels.value = {
        labelText: !props.isFurnitureShipping ? label : '', shipTo: shipText, shipFromSeller, deliveryDay: sddLabel, shipAvailable,
      };
      const ariaDescription = `${shipFromSeller} ${ label } ${sddLabel}`.trim();
      options.value = [{
        label: h(ShipItRadioLabelText, {
          shipTo: shipText,
          isEGiftCard: props.isEGiftCard,
          isParcelShipping: props.isParcelShipping,
          isFurnitureShipping: props.isFurnitureShipping,
          furnitureShippingMessage: props.isFurnitureShipping ? label : '',
          shipAvailable,
          zipCode: props.isInternationalShip ? '' : currentZipCode.value,
          freeShippingEligible: props.freeShippingEligible && props.copyClarityVueEnabled,
          isInternationalShip: props.isInternationalShip,
        }),
        id: (props.page === 'bag') ? `ship-it-${props.productId}-bag-${props.bagItemId}` : `ship-it-${props.productId}`,
        value,
        labelType: 'html',
        disabled,
        ariaLabel: disabled ? 'Not available for shipping' : `${ shipText ? shipText.substring(0, 7) : 'Ship' } ${ shipText ? `zipcode ${currentZipCode.value}` : ''}.`,
        ariaDescription,
      }];

      if (props.productAvailabilityInfo?.lowAvailability && props.page !== 'pdp') {
        isLowStock.value = true;

        if (brand.value === 'bcom') {
          lowStockLabel.value[0] = 'Just a few left!';
        } else {
          lowStockLabel.value[0] = 'Low stock ';
        }
        lowStockLabel.value[1] = radioLabels.value.labelText;
      } else if (isLowStock.value) {
        isLowStock.value = false;
      }

      if (props.productAvailabilityInfo?.orderType === 'POOL' || props.productAvailabilityInfo?.orderType === 'STR1') {
        copyClarityEnabled.value = true;
      } else {
        copyClarityEnabled.value = false;
      }

      const eventData = !props.isInternationalShip && props.shipEventData
      && eligibilityCheckForSTHBadging(props.shipEventData, props.deliveryDays, props.productCategoryId);
      if (eventData) {
        badgingMessage.value = eventData.message;
        badgingColor.value = eventData.color;
      } else {
        badgingMessage.value = '';
        badgingColor.value = '';
      }

      const sddBadgingData = !props.isInternationalShip && sddLabel && props.sddEventData
      && generateSDDMessageForBadging(props.sddEventData, props.productCategoryId, sddLabel);

      if (sddBadgingData) {
        sddColorForBadging.value = sddBadgingData.color;

        if (sddBadgingData.message) {
          nextTick(() => {
            sddMessageForBadging.value = sddBadgingData.message;
          });
        }
      } else {
        sddColorForBadging.value = '';
        sddMessageForBadging.value = '';
      }

      const isNotAvailableForShipping = options.value[0].disabled;
      if (isNotAvailableForShipping) {
        emit('option-change'); // emit to change the selected radio button when shipping is unavailable
      }
    }, { immediate: true });

    return {
      options,
      radioLabels,
      handleZipCodeSlideout,
      onUpdateZipcode,
      currentZipCode,
      ariaLabel,
      zipcodeSlideoutShown,
      onSlideoutClose,
      zipcodeSlideoutMounted,
      isLowStock,
      lowStockLabel,
      slideoutMount,
      copyClarityEnabled,
      badgingMessage,
      badgingColor,
      sddMessageForBadging,
      sddColorForBadging,
    };
  },

};
</script>

<style lang="scss" scoped>
.zipcode-button {
  margin-left: rem-calc(5);
}

.shipping-message {
  background-color: $background-color;
  align-items: center;
  padding: rem-calc(5) rem-calc(10);
}
.sdd-shipping-message {
  width: fit-content;
  padding: rem-calc(5) 0;
  flex-wrap: nowrap;
  margin-left: rem-calc(20);
}
.disabled{
  color: $gray-2-color;
}

.display{
  display: none;
}

.low-stock {
  color: $sale-color;
}

.event-badging {
  font-weight:bold;
}

</style>
